import { toast } from "react-toastify";

/** Matches the toast options used across the dashboard pages. */
const TOAST = {
  position: "top-right",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
};

/**
 * AdminModel.mobileNumber is a Number, so anything that is not a digit either
 * gets dropped or throws a CastError on save. Keep phone input digits-only.
 * 15 is the E.164 maximum; 7 is the shortest usable national number.
 */
export const PHONE_MIN = 7;
export const PHONE_MAX = 15;
export const PASSWORD_MIN = 8;

const EMAIL_RE = /^[\w.-]+@([\w-]+\.)+[\w-]{2,10}$/;

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";

/** Strip everything that is not a digit. */
export const digitsOnly = (v) => String(v ?? "").replace(/\D/g, "");

/* ------------------------------------------------------------------ rules --
 * Each builder returns a rule: (value) => message | null
 */

export const required = (label) => (v) =>
  isBlank(v) ? `${label} is required` : null;

export const email = (label = "Email") => (v) =>
  isBlank(v) || EMAIL_RE.test(String(v).trim())
    ? null
    : `${label} is not a valid email address`;

export const phone = (label = "Contact no.") => (v) => {
  if (isBlank(v)) return null;
  const value = String(v).trim();
  const digits = digitsOnly(value);
  if (digits.length !== value.length) return `${label} must contain digits only`;
  if (digits.length < PHONE_MIN || digits.length > PHONE_MAX)
    return `${label} must be ${PHONE_MIN}-${PHONE_MAX} digits`;
  return null;
};

export const number =
  (label, { min, max, integer = true } = {}) =>
  (v) => {
    if (isBlank(v)) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return `${label} must be a number`;
    if (integer && !Number.isInteger(n)) return `${label} must be a whole number`;
    if (min !== undefined && n < min) return `${label} must be at least ${min}`;
    if (max !== undefined && n > max) return `${label} must be at most ${max}`;
    return null;
  };

export const minLength = (label, n) => (v) =>
  isBlank(v) || String(v).length >= n
    ? null
    : `${label} must be at least ${n} characters`;

/** Guards select fields whose placeholder option carries a real value. */
export const oneOf = (label, allowed) => (v) =>
  isBlank(v) || allowed.includes(v)
    ? null
    : `Please choose a valid ${label.toLowerCase()}`;

export const url = (label = "Link") => (v) => {
  if (isBlank(v)) return null;
  try {
    const parsed = new URL(String(v).trim());
    return ["http:", "https:"].includes(parsed.protocol)
      ? null
      : `${label} must start with http:// or https://`;
  } catch {
    return `${label} must be a valid URL (including https://)`;
  }
};

/* ------------------------------------------------------------------ runner -- */

/** Run `[value, ...rules]` tuples and collect every failure message. */
export const collectErrors = (checks) =>
  checks.flatMap(([value, ...rules]) =>
    rules.map((rule) => rule(value)).filter(Boolean)
  );

/**
 * Validate a form and toast the first problem found.
 * Returns true when the form is safe to submit.
 *
 *   if (!validateForm([
 *     [data.name,  required("Name")],
 *     [data.email, required("Email"), email()],
 *   ])) return;
 */
export const validateForm = (checks) => {
  const errors = collectErrors(checks);
  if (errors.length) {
    toast.error(errors[0], TOAST);
    return false;
  }
  return true;
};

/* ------------------------------------------------------------- input props --
 * Spread onto LegendField/IconField. `title` is deliberately omitted: the field
 * components use `title` as the visible legend, so it must not be overwritten.
 */

/** Digits-only phone input, capped at PHONE_MAX, sanitised as the user types. */
export const phoneInputProps = (onValue) => ({
  type: "tel",
  inputMode: "numeric",
  autoComplete: "tel",
  maxLength: PHONE_MAX,
  pattern: `[0-9]{${PHONE_MIN},${PHONE_MAX}}`,
  onChange: (e) => {
    const clean = digitsOnly(e.target.value).slice(0, PHONE_MAX);
    // The field components render uncontrolled inputs (defaultValue), so the
    // DOM value has to be corrected here or rejected characters stay visible.
    if (clean !== e.target.value) e.target.value = clean;
    onValue(clean);
  },
});

/** Numeric input with browser-level min/max/step. */
export const numberInputProps = (onValue, { min = 0, max, step = 1 } = {}) => ({
  type: "number",
  inputMode: "numeric",
  min,
  step,
  ...(max !== undefined ? { max } : {}),
  onChange: (e) => onValue(e.target.value),
});
